import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { naviguer } from '../App';
import { ModeBadge, Solde, Montant } from '../components/Montant';
import { chf, dateCourte } from '../utils/format';
import type { ParticipantResume } from '../types';

type Filtre = 'tous' | 'cash' | 'facture' | 'dette' | 'aconfirmer';

// La liste : une ligne par personne en mesure SISP, avec son solde — ce que
// l'Excel montrait feuille par feuille, ici d'un seul regard.
export function ParticipantsPage() {
  const [personnes, setPersonnes] = useState<ParticipantResume[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState<Filtre>('tous');
  const [termines, setTermines] = useState(false);
  const [referent, setReferent] = useState<number | 'tous'>('tous');

  useEffect(() => {
    setLoading(true);
    api.getParticipants(termines)
      .then((p) => { setPersonnes(p); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Échec du chargement'))
      .finally(() => setLoading(false));
  }, [termines]);

  const referents = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of personnes) if (p.referent_id != null && p.referent) m.set(p.referent_id, p.referent);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [personnes]);

  const visibles = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return personnes.filter((p) => {
      if (needle && !`${p.prenom} ${p.nom} ${p.nom} ${p.prenom}`.toLowerCase().includes(needle)) return false;
      if (referent !== 'tous' && p.referent_id !== referent) return false;
      switch (filtre) {
        case 'cash': return p.mode === 'cash';
        case 'facture': return p.mode === 'facture';
        case 'dette': return p.solde < 0;
        case 'aconfirmer': return !p.mode_confirme;
        default: return true;
      }
    });
  }, [personnes, q, filtre, referent]);

  const totalDu = visibles.reduce((a, p) => a + (p.solde < 0 ? -p.solde : 0), 0);

  const chip = (f: Filtre, label: string) => (
    <button className={`chip ${filtre === f ? 'active' : ''}`} onClick={() => setFiltre(f)}>{label}</button>
  );

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading">
          <span className="page-title">Participant·e·s</span>
          <p className="page-meta">{visibles.length} personne{visibles.length > 1 ? 's' : ''} · total dû {chf(totalDu)}</p>
        </div>
      </div>

      <div className="toolbar">
        <div className="filter-chips">
          {chip('tous', 'Toutes')}
          {chip('cash', 'Cash')}
          {chip('facture', 'Facture')}
          {chip('dette', 'Avec solde dû')}
          {chip('aconfirmer', 'Mode à confirmer')}
        </div>
        {referents.length > 1 && (
          <select className="form-select" value={referent} onChange={(e) => setReferent(e.target.value === 'tous' ? 'tous' : Number(e.target.value))} style={{ width: 'auto' }}>
            <option value="tous">Tou·te·s les référent·e·s</option>
            {referents.map(([id, nom]) => <option key={id} value={id}>{nom}</option>)}
          </select>
        )}
        <label className="flex items-center gap-8 text-sm" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={termines} onChange={(e) => setTermines(e.target.checked)} /> Contrats terminés
        </label>
        <div className="search-wrapper ml-auto">
          <input className="search-input" placeholder="Rechercher un nom…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      {error && <div className="badge badge-err mb-12" style={{ display: 'block' }}>{error}</div>}

      {loading ? (
        <div className="spinner" />
      ) : visibles.length === 0 ? (
        <div className="empty-state">Aucune personne ne correspond.</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Participant·e</th>
              <th>Mode</th>
              <th className="num">Tarif</th>
              <th className="num" title="Séances suivies ce mois">Séances (mois)</th>
              <th className="num" title="Depuis la date d'ouverture">Dû</th>
              <th className="num" title="Depuis la date d'ouverture">Payé</th>
              <th className="num">Solde</th>
              <th>Dernier paiement</th>
              <th>Factures</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((p) => (
              <tr key={p.id} className="is-clickable" onClick={() => naviguer('participants', p.id)}>
                <td className="font-medium">
                  {p.prenom} {p.nom}{!p.actif && <span className="badge ml-8">terminé</span>}
                  <span className="cell-sub">{p.referent ?? 'sans référent·e'} · {dateCourte(p.mandat_dernier_debut ?? p.mandat_debut)} → {dateCourte(p.mandat_fin)}</span>
                </td>
                <td><ModeBadge mode={p.mode} confirme={p.mode_confirme} /></td>
                <td className="num text-sm"><Montant valeur={p.tarif_seance} /></td>
                <td className="num">{p.seances_mois_courant}</td>
                <td className="num text-sm"><Montant valeur={p.total_du} /></td>
                <td className="num text-sm"><Montant valeur={p.total_paye} /></td>
                <td className="num"><Solde valeur={p.solde} /></td>
                <td className="text-sm">{dateCourte(p.dernier_paiement)}</td>
                <td className="text-sm">
                  {p.factures_envoyees > 0 && <span className="badge badge-warn">{p.factures_envoyees} envoyée{p.factures_envoyees > 1 ? 's' : ''}</span>}
                  {p.factures_brouillon > 0 && <span className="badge ml-4">{p.factures_brouillon} brouillon</span>}
                  {p.factures_envoyees === 0 && p.factures_brouillon === 0 && <span className="text-ink-3">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
